import { useCallback, useRef, useState } from "react";
import { trackEngagement } from "../lib/engagement";
import { hostParams } from "../lib/hostQuery";

/**
 * A scheme's current sub-screen, with the move counted for content-engagement
 * analytics. A drop-in replacement for `useState<Screen>("home")` - every
 * scheme keeps its own screen union, and this adds the one thing they all
 * wanted: knowing which sections guests actually open.
 *
 * Only a *change* is counted, and only screen ids (never room, guest or PHI
 * values) leave the device. The initial "home" render is deliberately not
 * counted: it happens on every boot and idle-return, and would swamp the
 * numbers with a section nobody chose.
 */
/**
 * `?screen=<id>` - the screen to open on instead of home.
 *
 * Read here rather than in each template because every scheme already routes
 * through this hook, so one read deep-links all nine. Its job is the CMS live
 * preview: an operator designing the Custom screen should watch THAT page
 * change as they type, not a home screen with a tile on it. A fielded TV never
 * carries the parameter, and an unknown value simply renders nothing, which is
 * the same as an unreachable screen.
 */
function initialFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const v = hostParams().get("screen");
  return v && v.trim() ? v.trim() : null;
}

export function useTrackedScreen<S extends string>(
  initial: S
): [S, (next: S) => void] {
  const [screen, setScreen] = useState<S>(
    () => (initialFromUrl() as S | null) ?? initial
  );
  // Mirrors `screen`, but readable synchronously - so the dedupe check can't be
  // double-fired by React invoking a state updater twice in development.
  const lastRef = useRef<S>(screen);

  const go = useCallback((next: S) => {
    if (lastRef.current !== next) {
      lastRef.current = next;
      trackEngagement("section_view", next);
    }
    setScreen(next);
  }, []);

  return [screen, go];
}
